<template>
  <SimpleFormModal
    :title="title"
    :submit-button-text="submitButtonText"
    :open="isOpen"
    :disable-submit="submitDisabled"
    @close="handleClose"
    @submit="handleSubmit"
  >
    <template #content>
      <div class="wrapper">
        <div v-for="(input, index) in inputs" :key="input.key" class="input-container">
          <TextFieldStandard
            :focus="index === 0"
            :disabled="input.textFieldDisabled"
            :hideIconX="true"
            :label="input.label"
            :prefilledText="input.prefilledText"
            :required="input.required"
            :type="input.type"
            :error="validationResults[input.key]?.result === false"
            :error-message="'* ' + validationResults[input.key]?.message"
            :help-text="input.helpText"
            @emitInput="(value) => handleInput(input.key, value)"
          />
        </div>
      </div>
    </template>
  </SimpleFormModal>
</template>
<script setup lang="ts">
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import SimpleFormModal from './SimpleFormModal.vue'
import { computed, ref, toRefs, watch } from 'vue'
import { validateInput, Input } from './inputValidation'

type Props = {
  title: string
  inputs: Input[]
  submitButtonText?: string
  open?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitButtonText: 'Submit',
  validation: () => true,
  open: false
})

const { title, submitButtonText } = toRefs(props)

const emit = defineEmits<{
  (e: 'submit', values: Record<string, string | number>): void
  (e: 'close'): void
}>()

const isOpen = ref(props.open)
const values = ref<Record<string, string | number>>({})

const validationResults = computed<Record<string, { result: boolean; message?: string }>>(() => {
  const results: Record<string, { result: boolean; message?: string }> = {}
  props.inputs.forEach((input) => {
    const value = values.value[input.key]
    if (value !== undefined) {
      const validationResult = validateInput(input, value)
      results[input.key] = validationResult
    } else {
      results[input.key] = { result: true }
    }
  })
  return results
})

const isValid = computed(() => {
  return Object.values(validationResults.value).every((result) => result.result)
})

const submitDisabled = computed(() => {
  return (
    props.inputs.some((input) => {
      const value = values.value[input.key]
      return input.required && (!value || value === '')
    }) || !isValid.value
  )
})

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
  },
  { immediate: true }
)

const handleInput = (key: string, input: number | string) => {
  values.value[key] = input
}

const handleSubmit = () => {
  if (!submitDisabled.value) {
    isOpen.value = false
    emit('submit', values.value)
  }
}

const handleClose = () => {
  isOpen.value = false
  // Reset values when closing the modal
  values.value = {}
  // reste validation results
  for (const input of props.inputs) {
    validationResults.value[input.key] = { result: true }
  }
  // Emit close event
  emit('close')
}
</script>

<style scoped lang="scss">
.wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
